import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import ReportContent, { ReportMetrics } from '../components/ReportContent.tsx'

interface SharedReportData {
  report: {
    name: string
    companyName: string | null
    config: {
      sections: string[]
      dateRange: { from: string; to: string } | null
    }
  }
  data: ReportMetrics | null
}

// Strip a trailing /api so this stays correct whether REACT_APP_API_URL includes it
// (env.example's local-dev convention) or is unset (deployed environments) — see
// client/src/services/api.ts for the same normalization on the shared axios instance.
const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '')

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const SharedReport: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>()
  const [reportData, setReportData] = useState<SharedReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [expired, setExpired] = useState(false)
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    async function fetchReport() {
      try {
        const res = await axios.get(`${apiBase}/api/shared/report/${shareToken}`)
        if (res.data.success) {
          setReportData(res.data)
        }
      } catch (err: any) {
        if (err?.response?.status === 404 || err?.response?.data?.expired) {
          setExpired(true)
        } else {
          setFetchError(true)
        }
      } finally {
        setLoading(false)
      }
    }
    if (shareToken) fetchReport()
  }, [shareToken])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    )
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-600">We couldn't load this report. Please try again later.</p>
        </div>
      </div>
    )
  }

  if (expired || !reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Report Unavailable</h1>
          <p className="text-gray-600">
            This report link has expired or has been revoked. Please contact the sender for a new
            link.
          </p>
        </div>
      </div>
    )
  }

  const { report, data } = reportData
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No data available for this report.</p>
      </div>
    )
  }

  const dateLabel = data.period
    ? `${formatDate(data.period.start)} - ${formatDate(data.period.end)}`
    : ''

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-blue-700">Marzz AI</h1>
            {dateLabel && <span className="text-sm text-gray-500">{dateLabel}</span>}
          </div>
          <div className="mt-1">
            <h2 className="text-lg font-semibold text-gray-900">{report.name}</h2>
            {report.companyName && <p className="text-sm text-gray-500">{report.companyName}</p>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:px-6">
        <ReportContent sections={report.config.sections} data={data} />
      </main>

      <footer className="border-t border-gray-200 py-6 text-center">
        <p className="text-sm text-gray-400">
          Powered by <span className="font-semibold text-blue-600">Marzz AI</span>
        </p>
      </footer>
    </div>
  )
}

export default SharedReport
